import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { formatUuidT } from "../../flatbuffers/display";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const channelModifierAPI = createApi({
  reducerPath: "channelModifierAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetChannelModifiers"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL CHANNEL MODIFIERS */
    getChannelModifiers: builder.query({
      query: (body: {
        page: number;
        limit: number;
        token: string;
        searchAndFilterString?: string;
      }) => ({
        url: `/channel_modifiers/?sort=created_at&sort_direction=desc&page=${body.page}&limit=${
          body.limit
        }${body.searchAndFilterString ?? ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* GET ONE CHANNEL MODIFIER */
    getChannelModifier: builder.query({
      query: (body: { id: string; token: string }) => ({
        url: `/channel_modifiers/${body.id}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* CREATE CHANNEL MODIFIER */
    postChannelModifier: builder.mutation({
      query: (body: { body: Record<string, unknown>; token: string }) => ({
        url: "/channel_modifiers/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateChannelModifier" },
      invalidatesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* UPDATE CHANNEL MODIFIER */
    putChannelModifier: builder.mutation({
      query: (body: { body: Record<string, unknown>; token: string }) => ({
        url: `/channel_modifiers/${formatUuidT(body.body.id as string)}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateChannelModifier" },
      invalidatesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* DELETE CHANNEL MODIFIER */
    deleteChannelModifier: builder.mutation({
      query: (body: { id: string; token: string }) => ({
        url: `/channel_modifiers/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* GET MODIFIERS BY SOURCE CHANNEL */
    getChannelModifiersByChannel: builder.query({
      query: (body: { channelId: string; token: string }) => ({
        url: `/channels/${body.channelId}/modifiers`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* GET MODIFIERS BY SERVER (one page; the server joins on the source channel) */
    getChannelModifiersByServer: builder.query({
      query: (body: { serverId: string; page: number; limit: number; token: string }) => ({
        url: `/channel_modifiers/?server_id=${body.serverId}&page=${body.page}&limit=${body.limit}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannelModifiers"],
    }),
    //* *********************************************************** */
    //* BULK UPDATE CHANNEL MODIFIERS */
    bulkPutChannelModifiers: builder.mutation({
      query: (body: { body: Record<string, unknown>[]; token: string }) => ({
        url: "/channel_modifiers/bulk",
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetChannelModifiers"],
    }),
  }),
});

export const {
  useLazyGetChannelModifiersQuery,
  useLazyGetChannelModifierQuery,
  usePostChannelModifierMutation,
  usePutChannelModifierMutation,
  useDeleteChannelModifierMutation,
  useLazyGetChannelModifiersByChannelQuery,
  useBulkPutChannelModifiersMutation,
} = channelModifierAPI;
